import type { ModelInfo } from '@paradigm/terminal-core';

import { isFeatured } from './featured';
import {
  groupByModelAndVersion,
  parseModelId,
  type ModelVersionEntry,
  type ParsedModelId,
} from './provider-groups';

export interface BasePickerEntry {
  /** Curated display name — parent label for a flyout, or the flat item. */
  readonly label: string;
  readonly match: (p: ParsedModelId) => boolean;
}

/**
 * Curated base picker: a small, hand-ordered set surfaced above the full
 * catalog. Sections render top-to-bottom separated by a divider; an entry
 * (or a whole section) with no available model is skipped. Kimi 3 / GLM 5.2
 * intentionally pin the TEE provider — the other providers stay under
 * "More Models".
 */
export const BASE_PICKER_SECTIONS: readonly (readonly BasePickerEntry[])[] = [
  [
    {
      label: 'Fable 5',
      match: (p) => p.company === 'anthropic' && p.model === 'fable',
    },
    {
      label: 'Opus',
      match: (p) => p.company === 'anthropic' && p.model === 'opus',
    },
  ],
  [
    {
      label: 'GPT-5.6',
      match: (p) => p.company === 'openai' && p.model === 'gpt-5.6',
    },
    {
      label: 'GPT-5.5',
      match: (p) => p.company === 'openai' && p.model === 'gpt-5.5',
    },
  ],
  [
    {
      label: 'Kimi 3',
      match: (p) =>
        p.company === 'moonshot-ai' &&
        p.model === 'kimi-k3' &&
        p.provider === 'phala',
    },
    {
      label: 'GLM 5.2',
      match: (p) =>
        p.company === 'z-ai' &&
        p.model === 'glm-5.2' &&
        p.provider === 'near-ai',
    },
    {
      label: 'Qwen 3.7 Max',
      match: (p) => p.company === 'qwen' && p.model === 'qwen-3.7',
    },
    { label: 'DeepSeek', match: (p) => p.company === 'deepseek' },
  ],
];

/**
 * Selectable versions for a base entry, most-capable first (reuses the
 * catalog sort so DeepSeek renders V4 Flash > V3.2 > V3). Empty when the
 * model isn't in the available catalog.
 */
export function resolveBaseVersions(
  models: readonly ModelInfo[],
  entry: BasePickerEntry,
): readonly ModelVersionEntry[] {
  const matched = models.filter((m) => entry.match(parseModelId(m.id)));
  if (matched.length === 0) return [];
  return groupByModelAndVersion(matched).flatMap((g) => g.versions);
}

/**
 * The picker's default list: models the gateway tags `Featured: True`, sorted
 * most-capable first (reuses the catalog sort). Data-driven so the catalog
 * owner curates the default list through LiteLLM tags without a UI release.
 * Empty when nothing is tagged (or the source carries no tags) — the picker
 * then falls back to the hand-curated BASE_PICKER_SECTIONS.
 */
export function resolveFeaturedVersions(
  models: readonly ModelInfo[],
): readonly ModelVersionEntry[] {
  const featured = models.filter((m) => isFeatured(m.tags));
  if (featured.length === 0) return [];
  return groupByModelAndVersion(featured).flatMap((g) => g.versions);
}
